import React, { useState, Suspense } from "react";
import { useTranslation } from "react-i18next";
// Lazy load des icônes
import { createLazyIcon } from "../../lib/lazy-icons";

const Calendar = createLazyIcon("Calendar");
const Clock = createLazyIcon("Clock");
const Users = createLazyIcon("Users");
const Check = createLazyIcon("Check");
const ExternalLink = createLazyIcon("ExternalLink");

// Wrapper pour icônes lazy
const LazyIconWrapper = ({
  Icon,
  className,
  ...props
}: {
  Icon: React.LazyExoticComponent<
    React.ComponentType<React.SVGProps<SVGSVGElement>>
  >;
  className?: string;
} & Omit<React.SVGProps<SVGSVGElement>, "ref">) => (
  <Suspense fallback={<span className={className || "w-5 h-5"} />}>
    <Icon className={className} {...props} />
  </Suspense>
);
import { Link } from "react-router-dom";
import PollCreator from "../PollCreator";
import FormPollCreator from "../polls/FormPollCreator";
import { AvailabilityPollCreatorContent } from "../../exports/products";
import { useEditorActions } from "./EditorStateProvider";
import { useToast } from "@core/hooks/use-toast";
import { addPoll, type Poll, type PollSettings } from "@core/lib/pollStorage";
import { logger } from "@core/lib/logger";
import { CtaTracker } from "../../services/CtaTracker";

interface PollPreviewProps {
  poll: Poll;
}

/**
 * Composant Preview pour afficher l'interface d'édition du sondage
 * Utilise les composants existants PollCreator/FormPollCreator
 */
export function PollPreview({ poll }: PollPreviewProps) {
  const { setCurrentPoll } = useEditorActions();
  const { toast } = useToast();
  const { t } = useTranslation("polls");
  const [published, setPublished] = useState(false);
  const [publishedPoll, setPublishedPoll] = useState<Poll | null>(null);

  // Écran de succès après publication (Session 2)
  if (published && publishedPoll) {
    const pollType = publishedPoll.type === "form" ? "Formulaire" : "Sondage";
    const pollUrl = `/poll/${publishedPoll.slug || publishedPoll.id}`;

    return (
      <div
        className="min-h-[400px] bg-background flex items-center justify-center p-8"
        data-testid="form-success-screen"
      >
        <div className="max-w-2xl w-full">
          <div className="bg-card rounded-lg border border-border p-8 text-center space-y-6">
            {/* Icône de succès */}
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center">
                <LazyIconWrapper
                  Icon={Check}
                  className="w-10 h-10 text-success"
                />
              </div>
            </div>

            {/* Message de succès */}
            <div>
              <h1
                className="text-3xl font-bold text-foreground mb-2"
                data-testid="success-message"
              >
                {publishedPoll.type === "form"
                  ? t("preview.success.title.form")
                  : publishedPoll.type === "availability"
                    ? t("preview.success.title.availability")
                    : t("preview.success.title.poll")}
              </h1>
              <p className="text-muted-foreground">
                {publishedPoll.type === "form"
                  ? t("preview.success.description.form", {
                      title: publishedPoll.title,
                    })
                  : publishedPoll.type === "availability"
                    ? t("preview.success.description.availability", {
                        title: publishedPoll.title,
                      })
                    : t("preview.success.description.poll", {
                        title: publishedPoll.title,
                      })}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Link
                to={
                  publishedPoll.type === "form"
                    ? "/form/dashboard"
                    : publishedPoll.type === "availability"
                      ? "/availability/dashboard"
                      : "/date/dashboard"
                }
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-success hover:bg-success/90 text-success-foreground rounded-lg font-semibold transition-all shadow-lg"
                data-testid="go-to-dashboard"
              >
                <LazyIconWrapper Icon={Check} className="w-5 h-5" />
                {t("preview.success.goToDashboard")}
              </Link>
              <Link
                to={pollUrl}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent text-muted-foreground border-2 border-border rounded-lg font-semibold hover:bg-accent transition-colors"
                data-testid="view-poll-button"
              >
                <LazyIconWrapper Icon={ExternalLink} className="w-5 h-5" />
                {publishedPoll.type === "form"
                  ? t("preview.success.view.form")
                  : t("preview.success.view.poll")}
              </Link>
            </div>

            {/* Lien de partage */}
            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">
                {t("preview.success.shareLink")}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                <code className="px-4 py-2 bg-muted border border-border rounded text-sm font-mono text-muted-foreground break-all">
                  {window.location.origin}/poll/
                  {publishedPoll.slug || publishedPoll.id}
                </code>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/poll/${publishedPoll.slug || publishedPoll.id}`;
                    navigator.clipboard.writeText(url);
                    CtaTracker.trackShared(publishedPoll.id, "copy");
                    toast({
                      title: t("preview.success.copied.title"),
                      description: t("preview.success.copied.description"),
                    });
                  }}
                  className="px-4 py-2 bg-success hover:bg-success/90 text-success-foreground rounded-lg transition-colors text-sm font-medium whitespace-nowrap"
                  data-testid="copy-link"
                >
                  {t("preview.success.copy")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="bg-card p-8 rounded-lg border-2 border-dashed border-border text-center">
        <p className="text-muted-foreground">{t("preview.empty.noPoll")}</p>
      </div>
    );
  }

  const handleSave = (draft: Partial<Poll>) => {
    try {
      // Mettre à jour le poll avec les nouvelles données
      const updatedPoll = {
        ...poll,
        ...draft,
        updated_at: new Date().toISOString(),
      };

      // Sauvegarder dans localStorage
      addPoll(updatedPoll as Poll);

      // Mettre à jour le contexte
      setCurrentPoll(updatedPoll as Poll);

      toast({
        title: t("preview.save.success.title"),
        description: t("preview.save.success.description"),
      });
    } catch (error) {
      logger.error("Erreur sauvegarde", error);
      toast({
        title: t("preview.save.error.title"),
        description: t("preview.save.error.description"),
        variant: "destructive",
      });
    }
  };

  const handleFinalize = (draft: Partial<Poll>, savedPoll?: Poll) => {
    try {
      // Utiliser le poll sauvegardé s'il est fourni (FormPollCreator), sinon créer
      const finalizedPoll = savedPoll || {
        ...poll,
        ...draft,
        status: "active" as const,
        updated_at: new Date().toISOString(),
      };

      // Sauvegarder dans localStorage si pas déjà fait
      if (!savedPoll) {
        addPoll(finalizedPoll as Poll);
      }

      // Mettre à jour le contexte
      setCurrentPoll(finalizedPoll as Poll);

      // Afficher l'écran de succès (Session 2)
      setPublishedPoll(finalizedPoll as Poll);
      setPublished(true);
    } catch (error) {
      logger.error("Erreur finalisation", error);
      toast({
        title: t("preview.save.error.title"),
        description: t("preview.finalize.error.description"),
        variant: "destructive",
      });
    }
  };

  // Preview pour sondage de dates - UTILISER L'EXPÉRIENCE EXISTANTE
  if (poll.type === "date") {
    // Convertir le format StoragePoll vers le format attendu par PollCreator
    const initialData = {
      title: poll.title,
      type: poll.type,
      dates: poll.dates || poll.settings?.selectedDates || [],
      timeSlots: poll.settings?.timeSlotsByDate
        ? Object.entries(poll.settings.timeSlotsByDate).flatMap(
            ([date, slots]: [
              string,
              Array<{
                hour: number;
                minute: number;
                enabled: boolean;
                duration?: number;
              }>,
            ]) =>
              slots.map(
                (slot: {
                  hour: number;
                  minute: number;
                  enabled: boolean;
                  duration?: number;
                }) => {
                  // Calculer l'heure de fin en fonction de la durée si disponible
                  // Sinon, par défaut 1 heure
                  const duration = slot.duration || 60; // durée en minutes
                  const endHour = slot.hour + Math.floor(duration / 60);
                  const endMinute = slot.minute + (duration % 60);

                  return {
                    start: `${String(slot.hour).padStart(2, "0")}:${String(slot.minute).padStart(2, "0")}`,
                    end: `${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(2, "0")}`,
                    dates: [date],
                  };
                },
              ),
          )
        : [],
    };

    return (
      <div className="bg-card rounded-lg shadow-sm">
        {/* Utiliser le PollCreator existant */}
        {/* Key basée sur updated_at pour forcer le remontage quand le poll change */}
        <PollCreator
          key={`${poll.id}-${poll.updated_at}`}
          initialData={initialData}
          onBack={() => {
            // Callback appelé après finalisation (Session 2)
            handleFinalize({}, poll);
          }}
        />
      </div>
    );
  }

  // Preview pour questionnaire/formulaire - UTILISER L'EXPÉRIENCE EXISTANTE
  if (poll.type === "form") {
    return (
      <div
        className="bg-card rounded-lg shadow-sm"
        data-poll-preview
        data-testid="poll-preview"
      >
        <FormPollCreator
          initialDraft={poll as any}
          onCancel={() => {
            // Callback appelé après annulation
            setCurrentPoll(poll);
          }}
          onFinalize={(draft, savedPoll) => {
            if (savedPoll) {
              handleFinalize(draft as any, savedPoll);
            }
          }}
        />
      </div>
    );
  }

  // Preview pour sondage de disponibilités
  if (poll.type === "availability") {
    return (
      <div
        className="bg-card rounded-lg shadow-sm"
        data-poll-preview
        data-testid="poll-preview"
      >
        <AvailabilityPollCreatorContent
          initialData={{
            title: poll.title,
            description: poll.description,
          }}
          onBack={(createdPoll) => {
            if (createdPoll) {
              handleFinalize({}, createdPoll);
            }
          }}
        />
      </div>
    );
  }

  // Fallback pour types inconnus
  return (
    <div className="bg-card p-8 rounded-lg border-2 border-dashed border-border text-center">
      <p className="text-muted-foreground">
        {t("preview.empty.unsupportedType", { type: poll.type })}
      </p>
    </div>
  );
}
